/**
 * directive-dispatch — фоновый тракт отправки директивы создателя.
 *
 * Корень класса «модал висит, пока Падла думает» (жалоба ректора 21 авг 2026):
 * /creator-message держит HTTP-ответ весь ход Падлы (10–50 с, единая машина),
 * а CreatorDirectiveDialog ждал этот промис с открытым окном; в шапке чата
 * (Viewer) addToast вообще не передавался — ни «работаю», ни ошибки.
 *
 * Новый контракт UI: кнопка = «принято в работу», окно закрывается сразу;
 * промис живёт ЗДЕСЬ (модульный стор, переживает размонтирование диалога),
 * ход и итог показывает DirectiveStatusHost (чипы в углу экрана, смонтирован
 * один раз в AdminPanel). Ошибка НЕ теряет текст: провальная отправка хранится,
 * чип даёт «Повторить», а заново открытый диалог того же чата предзаполняется
 * этим текстом (getFailedDirectiveFor).
 *
 * Тот же паттерн честности, что use-topup-tracker: «отправлено» говорим только
 * по факту ответа сервера, «работаю» — сразу и явно.
 */

import {
  sendCreatorDirective,
  type CreatorDirectiveResult,
  describeCard,
  describeDeferred,
  describeQueued,
  type SendCreatorDirectiveArgs,
} from './creatorDirective';

export type DirectiveDispatchStatus = 'sending' | 'sent' | 'error';

export interface DirectiveDispatch {
  id: string;
  chatId: number;
  displayName: string | null;
  directive: string;
  withTools: boolean;
  /** Голосом — Падла говорит, а не пишет. */
  voice: boolean;
  status: DirectiveDispatchStatus;
  /** Человеческая строка итога: успех («модель, секунды») или причина ошибки. */
  note: string | null;
  startedAt: number;
  /** Полный payload — для «Повторить» без участия диалога. */
  args: SendCreatorDirectiveArgs;
}

/** Сколько успешный чип висит перед самоудалением. */
export const SENT_LINGER_MS = 8000;

let seq = 0;
const entries = new Map<string, DirectiveDispatch>();
const listeners = new Set<() => void>();
/** Стабильный снапшот для useSyncExternalStore (новый ref только на мутации). */
let snapshot: readonly DirectiveDispatch[] = [];

function emit(): void {
  snapshot = Array.from(entries.values());
  for (const cb of listeners) cb();
}

export function subscribeDispatches(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function getDispatches(): readonly DirectiveDispatch[] {
  return snapshot;
}

/** Человеческая причина провала. 504 и сетевой обрыв (status 0) — оба случая,
 *  где сервер мог ДОСЛАТЬ сообщение после потери связи (gateway доводит отправку
 *  до конца, даже когда клиент отвалился — разбор 01.09.2026): клиент НЕ ЗНАЕТ,
 *  дошло или нет, и не имеет права утверждать «не отправлено». Честно зовём
 *  проверить ленту, чтобы админ не слал дубль. */
function failureNote(r: CreatorDirectiveResult): string {
  if (r.status === 504) {
    return 'Падла думал дольше минуты — ответ мог всё же дойти. Проверь ленту чата, прежде чем отправлять заново.';
  }
  if (r.status === 403) return r.error ?? 'Получатель заблокировал бота.';
  if (r.status === 422 && r.voiceForbidden) {
    return 'Голос не дошёл: у человека в Telegram запрещены голосовые от ботов. Падла отправил ему объяснение, как включить.';
  }
  if (r.status === 0) {
    return `Связь оборвалась, ответа нет — директива МОГЛА дойти. Проверь ленту чата, прежде чем отправлять заново (${r.error ?? 'network'}).`;
  }
  return `Ошибка: ${r.error ?? `HTTP ${r.status}`}`;
}

function successNote(r: CreatorDirectiveResult): string {
  if (r.deferred) return describeDeferred(r);
  if (r.queued) return describeQueued();
  if (r.card) return describeCard(r);
  if (r.handled) return 'выполнено без отдельного сообщения';
  const model = r.model ?? 'LLM';
  const secs = r.totalMs != null ? `${Math.max(1, Math.round(r.totalMs / 1000))} с` : '';
  const base = secs ? `${model}, ${secs}` : model;
  return r.channel === 'voice' ? `голосом — ${base}` : base;
}

/**
 * Запустить отправку в фоне. Возвращает id немедленно — вызывающий закрывает
 * своё окно, не дожидаясь сервера. Провальная запись того же чата снимается
 * (её текст уже в руках у отправителя).
 */
export function dispatchDirective(
  args: SendCreatorDirectiveArgs,
  displayName?: string | null,
): string {
  const prevFailed = getFailedDirectiveFor(args.chatId);
  if (prevFailed) entries.delete(prevFailed.id);

  const id = `d${++seq}`;
  const entry: DirectiveDispatch = {
    id,
    chatId: args.chatId,
    displayName: displayName ?? null,
    directive: args.directive,
    withTools: args.withTools,
    voice: args.outputMode === 'voice',
    status: 'sending',
    note: null,
    startedAt: Date.now(),
    args,
  };
  entries.set(id, entry);
  emit();

  void sendCreatorDirective(args)
    .catch((e): CreatorDirectiveResult => ({
      ok: false,
      status: 0,
      error: e instanceof Error ? e.message : String(e),
    }))
    .then(result => {
      const cur = entries.get(id);
      if (!cur) return; // сняли вручную, пока летело
      if (result.ok) {
        entries.set(id, { ...cur, status: 'sent', note: successNote(result) });
        emit();
        setTimeout(() => {
          if (entries.get(id)?.status === 'sent') {
            entries.delete(id);
            emit();
          }
        }, SENT_LINGER_MS);
      } else {
        entries.set(id, { ...cur, status: 'error', note: failureNote(result) });
        emit();
      }
    });

  return id;
}

/** Повторить провальную отправку тем же payload'ом. */
export function retryDispatch(id: string): void {
  const cur = entries.get(id);
  if (!cur || cur.status !== 'error') return;
  entries.delete(id);
  emit();
  dispatchDirective(cur.args, cur.displayName);
}

export function dismissDispatch(id: string): void {
  if (entries.delete(id)) emit();
}

/** Последняя провальная отправка этого чата — для предзаполнения диалога. */
export function getFailedDirectiveFor(chatId: number): DirectiveDispatch | null {
  let found: DirectiveDispatch | null = null;
  for (const e of entries.values()) {
    if (e.chatId === chatId && e.status === 'error') found = e;
  }
  return found;
}

/** Только для тестов: обнулить стор. */
export function __resetDispatchesForTests(): void {
  entries.clear();
  snapshot = [];
  // слушателей не трогаем — их снимает сам тест/анмаунт
}
